// src/components/settings/shared/SettingsHeaderRow.tsx
import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Popover,
  Button,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import { AccountActionButton as SettingsActionButton } from '../../common/Buttons';

interface SettingsHeaderRowProps {
  title: string;
  icon?: React.ReactNode;
  titleColor?: string;
  iconColor?: string;
  iconSize?: string;
  onAdd?: () => void;
  addLabel?: string;
  buttonMinWidth?: string;
  rightContent?: React.ReactNode;
  onSearch?: (text: string) => void;
  onClearSearch?: () => void;
  searchText?: string;
  searchPlaceholder?: string;
}

const HeaderRowContainer = styled(Box)({
  padding: '16px',
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  '@media (max-width: 600px)': {
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

const SearchPopoverTextField = styled(TextField)(({ theme }) => ({
  width: '300px',
  flexShrink: 0,
  '& .MuiOutlinedInput-root': {
    height: '48px',
    backgroundColor:
      theme.palette.mode === 'dark'
        ? theme.palette.grey[800]
        : theme.palette.grey[100],
    borderRadius: '4px 0 0 4px',
    '& fieldset': {
      borderColor: theme.palette.primary.light,
      borderWidth: '2px',
      borderRight: 'none',
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
      borderRight: 'none',
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      borderWidth: '2px',
      borderRight: 'none',
    },
  },
  '& .MuiOutlinedInput-input': {
    fontSize: '1.6rem',
    fontWeight: 400,
    padding: '0 0.5rem',
    width: '100%',
    minWidth: '0',
    color: theme.palette.text.primary,
    '&::placeholder': {
      opacity: 0.6,
      fontWeight: '600',
    },
  },
}));

const SearchPopoverButton = styled(Button)(({ theme }) => ({
  minWidth: '100px',
  height: '48px',
  borderRadius: '0 4px 4px 0',
  fontSize: '1.3rem',
  fontWeight: 600,
  letterSpacing: 'normal',
  padding: '0 1rem',
  flexShrink: 0,
  boxShadow: 'none',
  transition: 'letter-spacing 0.2s ease',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.grey[0] || '#fff',
  '&:hover': {
    transform: 'none !important',
    boxShadow: 'none',
    letterSpacing: '2px',
    backgroundColor: theme.palette.primary.dark,
  },
}));

const SearchIconButton = styled(IconButton)(({ theme }) => ({
  color:
    theme.palette.mode === 'dark'
      ? theme.palette.grey[300]
      : theme.palette.primary.main,
  '& svg': {
    fontSize: '2rem',
    transition: 'transform 0.2s ease',
  },
  '&:hover': {
    backgroundColor: 'transparent',
    '& svg': { transform: 'scale(1.25)' },
  },
}));

const ActiveSearchChip = styled(Chip)(({ theme }) => ({
  fontSize: '0.85rem',
  fontWeight: 600,
  maxWidth: '200px',
  backgroundColor:
    theme.palette.mode === 'dark'
      ? theme.palette.primary.dark
      : theme.palette.primary.light + '40',
  color: theme.palette.text.primary,
}));

const SettingsHeaderRow: React.FC<SettingsHeaderRowProps> = ({
  title,
  icon,
  titleColor,
  iconColor,
  iconSize = '3rem',
  onAdd,
  addLabel = 'Add',
  buttonMinWidth,
  rightContent,
  onSearch,
  onClearSearch,
  searchText = '',
  searchPlaceholder,
}) => {
  const theme = useTheme();
  const defaultTitleColor =
    theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.primary.dark;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [localSearchText, setLocalSearchText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const popoverOpen = Boolean(anchorEl);

  useEffect(() => {
    if (popoverOpen) {
      setLocalSearchText(searchText);
      const timer = setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          if (inputRef.current.value) inputRef.current.select();
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [popoverOpen, searchText]);

  const handleSearchSubmit = () => {
    onSearch?.(localSearchText);
    setAnchorEl(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearchSubmit();
    }
    if (e.key === 'Escape') {
      setAnchorEl(null);
    }
  };

  const handleClearInput = () => {
    setLocalSearchText('');
    inputRef.current?.focus();
  };

  const handleClearSearch = () => {
    setLocalSearchText('');
    onClearSearch?.();
  };

  const placeholder = searchPlaceholder || `Search ${title.toLowerCase()}...`;

  const hasRightContent = !!(onAdd || onSearch || rightContent);

  return (
    <HeaderRowContainer
      sx={{ padding: hasRightContent ? '16px' : '16px 16px 16px' }}
    >
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        {icon && (
          <Box
            sx={{
              color: iconColor || theme.palette.secondary.main,
              fontSize: iconSize,
              display: 'flex',
              alignItems: 'center',
              '& > svg': { fontSize: 'inherit' },
            }}
          >
            {icon}
          </Box>
        )}
        <Typography
          sx={{ lineHeight: 1.2 }}
          variant="h6"
          fontWeight={700}
          fontSize="2rem"
          color={titleColor || defaultTitleColor}
        >
          {title}
        </Typography>

        {searchText && (
          <ActiveSearchChip
            label={`"${searchText}"`}
            onDelete={handleClearSearch}
            size="small"
          />
        )}
      </Box>

      {hasRightContent && (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            ml: 'auto',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'flex-end',
            '@media (max-width: 600px)': {
              ml: 0,
              my: 2,
              width: '99%',
              flexDirection: 'column',
              alignItems: 'center',
              '& > button': { width: '100%' },
            },
          }}
        >
          {onSearch && (
            <SearchIconButton
              onClick={(e) => setAnchorEl(e.currentTarget)}
              aria-label="Search"
            >
              <SearchIcon />
            </SearchIconButton>
          )}
          {onAdd && (
            <SettingsActionButton
              startIcon={<AddIcon />}
              bgColor={theme.palette.primary.main}
              textColor={theme.palette.grey[0] || '#fff'}
              hoverBgColor={theme.palette.primary.dark}
              hoverTextColor="#fff"
              minWidth={buttonMinWidth}
              onClick={onAdd}
            >
              {addLabel}
            </SettingsActionButton>
          )}
          {rightContent}
        </Box>
      )}

      <Popover
        open={popoverOpen}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        disableRestoreFocus
        sx={{
          '& .MuiPopover-paper': {
            mt: '4px',
            overflow: 'visible',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', padding: '0.5rem' }}>
          <SearchPopoverTextField
            inputRef={inputRef}
            variant="outlined"
            placeholder={placeholder}
            value={localSearchText}
            onChange={(e) => setLocalSearchText(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{
                      fontSize: '1.8rem',
                      color: theme.palette.primary.main,
                    }}
                  />
                </InputAdornment>
              ),
              endAdornment: localSearchText && (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleClearInput}
                    size="small"
                    aria-label="Clear search"
                    edge="end"
                  >
                    <ClearIcon sx={{ fontSize: '1.6rem' }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <SearchPopoverButton
            onClick={handleSearchSubmit}
            variant="contained"
            color="primary"
          >
            Search
          </SearchPopoverButton>
        </Box>
      </Popover>
    </HeaderRowContainer>
  );
};

export default SettingsHeaderRow;
